// employee-data.service.ts
import { Injectable } from '@angular/core';
import {
  MemberData,
  UploadResponse,
  EmployeeSearchCriteriaRequest,
} from '../../models/employee.data.interface';
import { forkJoin, Observable } from 'rxjs';
import { ApiResponse } from '../../models/api-response.interface';
import { UrlReferenceService } from './url-reference.service';
import { HttpClient, HttpResponse } from '@angular/common/http';
import {
  ApiStatisticsResponse,
  HealthDeclarationResponse,
} from '../../models/member.model';

@Injectable({ providedIn: 'root' })
export class EmployeeDataService {
  constructor(
    private urlReferenceService: UrlReferenceService,
    private http: HttpClient
  ) {}
  employeeData: any = null;
  selectedFile: any | null = null;

  setEmployeeData(data: any) {
    this.employeeData = data;
  }

  getEmployeeData() {
    return this.employeeData;
  }

  setSelectedFile(file: any) {
    this.selectedFile = file;
  }

  isEmployeeDataAvailable(): boolean {
    const data = this.employeeData;
    return !!(
      data &&
      data.content &&
      Array.isArray(data.content.employee) &&
      data.content.employee.length > 0
    );
  }

  getEmployeeDataPaged(
    companyId: string,
    page: number,
    size: number
  ): Observable<ApiResponse<MemberData>> {
    const url = `${this.urlReferenceService.getEmployeesByCompanyUrl(
      companyId
    )}?page=${page}&size=${size}`;
    return this.http.post<ApiResponse<MemberData>>(url, {});
  }

  searchEmployeesWithCriteria(
    companyId: string,
    page: number,
    size: number,
    searchCriteria?: EmployeeSearchCriteriaRequest
  ): Observable<ApiResponse<MemberData>> {
    const url = `${this.urlReferenceService.searchEmployeesByCompanyUrl(
      companyId
    )}?page=${page}&size=${size}`;
    return this.http.post<ApiResponse<MemberData>>(url, searchCriteria || {});
  }

  deleteSelectedMembers(
    companyId: string | undefined,
    idsMap: Map<string, string>
  ): Observable<any> {
    const deleteRequests = Array.from(idsMap.entries()).map(([id, type]) =>
      this.http.delete(
        `${this.urlReferenceService.currentBaseUrl}/${companyId}/${type}/${id}`,
        { responseType: 'text' }
      )
    );

    return forkJoin(deleteRequests);
  }

  deleteSingleMember(
    companyId: string | undefined,
    itemType: string,
    id: string | number | undefined
  ): Observable<any> {
    const url = `${this.urlReferenceService.currentBaseUrl}/${companyId}/${itemType}/${id}`;
    return this.http.delete(url, { responseType: 'text' });
  }

  getMembers(
    companyId: string,
    page: number,
    size: number,
    filterParams: any
  ): Observable<ApiStatisticsResponse> {
    const url = `${this.urlReferenceService.getEmployeesByCompanyUrl(
      companyId
    )}?page=${page}&size=${size}`;
    return this.http.post<ApiStatisticsResponse>(url, filterParams);
  }

  getHealthDeclarations(): Observable<HealthDeclarationResponse[]> {
    const url = `${this.urlReferenceService.getHealthDeclarationUrl()}`;
    return this.http.get<HealthDeclarationResponse[]>(url);
  }

  getEmployees(companyId: string): Observable<any[]> {
    const url = this.urlReferenceService.getEmployeesByCompanyUrl(companyId);
    return this.http.post<any[]>(url, {});
  }

  uploadEmployeeFile(
    companyId: string,
    file: File
  ): Observable<HttpResponse<UploadResponse>> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const url = `${this.urlReferenceService.getMemberUrl}/upload/${companyId}`;
    return this.http.post<UploadResponse>(url, formData, {
      observe: 'response',
    });
  }

  saveMember(companyId: string, payload: any): Observable<any> {
    if (!companyId) {
      throw new Error('Company ID is required to save member');
    }
    const url = `${this.urlReferenceService.getMemberUrl}/company/${companyId}/save-member`;
    return this.http.post(url, payload);
  }
}
